import { useEffect, useState, useRef } from 'react';

interface OneSignalUser {
  id: string;
  subscriptionId?: string;
}

interface UseOneSignalReturn {
  isInitialized: boolean;
  userId: string | null;
  isSubscribed: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  onNotificationReceived?: (notification: any) => void;
  setOnNotificationReceived?: (callback: (notification: any) => void) => void;
}

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => void>;
    OneSignal?: any;
  }
}

export const useOneSignal = (): UseOneSignalReturn => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [onNotificationReceived, setOnNotificationReceived] = useState<((notification: any) => void) | undefined>(undefined);
  const notificationCallbackRef = useRef<((notification: any) => void) | undefined>(undefined);

  useEffect(() => {
    let permissionInterval: NodeJS.Timeout | null = null;
    let checkInterval: NodeJS.Timeout | null = null;

    // Wait for OneSignal to be available
    const initOneSignal = async () => {
      // Check if OneSignal is already loaded
      if (window.OneSignal) {
        await setupOneSignal();
        return;
      }

      // Wait for OneSignalDeferred to execute
      checkInterval = setInterval(() => {
        if (window.OneSignal) {
          if (checkInterval) clearInterval(checkInterval);
          setupOneSignal();
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
        if (!window.OneSignal) {
          console.warn('OneSignal failed to load');
        }
      }, 5000);
    };

    const setupOneSignal = async () => {
      try {
        const OneSignal = window.OneSignal;
        
        // Wait a bit for OneSignal to be fully ready
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check notification permission using browser API
        if ('Notification' in window) {
          const permission = Notification.permission;
          setIsSubscribed(permission === 'granted');
        }

        // IMPORTANT: OneSignal v16 doesn't have setNotificationWillShowHandler
        // We need to use Service Worker messages to intercept notifications
        console.log('🔧 Setting up notification handlers...');
        console.log('🔧 OneSignal object:', OneSignal);
        console.log('🔧 OneSignal methods:', Object.keys(OneSignal).filter(k => typeof OneSignal[k] === 'function'));
        
        // Try to get user ID - in v16 it might be different
        try {
          // Try different methods to get user ID
          if (OneSignal.User && OneSignal.User.onesignalId) {
            const user = OneSignal.User.onesignalId;
            if (user) {
              setUserId(user);
              console.log('✅ Got userId from OneSignal.User.onesignalId:', user);
            }
          } else if (OneSignal.getUserId && typeof OneSignal.getUserId === 'function') {
            const user = await OneSignal.getUserId();
            if (user) {
              setUserId(user);
              console.log('✅ Got userId from getUserId():', user);
            }
          } else {
            // Wait a bit and try again
            setTimeout(async () => {
              try {
                if (OneSignal.User && OneSignal.User.onesignalId) {
                  const user = OneSignal.User.onesignalId;
                  if (user) {
                    setUserId(user);
                    console.log('✅ Got userId (delayed) from OneSignal.User.onesignalId:', user);
                  }
                }
              } catch (e) {
                console.log('Could not get user ID:', e);
              }
            }, 2000);
          }
        } catch (e) {
          console.log('Could not get user ID:', e);
        }

        // Listen for permission changes using browser API
        if ('Notification' in window) {
          const checkPermission = () => {
            const permission = Notification.permission;
            setIsSubscribed(permission === 'granted');
          };

          // Check permission periodically
          permissionInterval = setInterval(checkPermission, 2000);
        }

        // In OneSignal v16, we need to use Service Worker to intercept notifications
        // Set up Service Worker message listener to catch notifications
        if ('serviceWorker' in navigator) {
          console.log('✅ Setting up Service Worker message listener');
          
          // Only set up listener once
          const messageHandler = (event: any) => {
            console.log('🔔🔔🔔 Service Worker message received:', event.data);
            
            // OneSignal sends notification data through Service Worker
            if (!event.data || !notificationCallbackRef.current) {
              return;
            }
            
            const data = event.data;
            
            // Check if it's a OneSignal notification - be very strict
            // Only trigger if it's explicitly a OneSignal notification type
            // or has both title and body (not empty and not default values)
            const isOneSignalNotification = data.type === 'ONESIGNAL_NOTIFICATION';
            const hasValidTitle = data.title && 
                                 data.title.trim() !== '' && 
                                 data.title !== 'Nueva notificación';
            const hasValidBody = data.body && data.body.trim() !== '';
            const hasValidContent = hasValidTitle && hasValidBody;
            
              if (isOneSignalNotification || hasValidContent) {
                console.log('✅ OneSignal notification detected, showing in-app');
                const notificationData = {
                  title: data.title,
                  body: data.body || data.message || '',
                  data: data.additionalData || data.data || {},
                };
                // Only call callback if we have a valid notification
                if (notificationData.title && notificationData.title !== 'Nueva notificación') {
                  notificationCallbackRef.current(notificationData);
                } else {
                  console.log('⚠️ Ignoring notification with invalid title');
                }
              } else {
                console.log('⚠️ Ignoring non-OneSignal message:', data);
              }
          };
          
          navigator.serviceWorker.addEventListener('message', messageHandler);
          
          // Also listen for notification clicks from Service Worker
          const clickHandler = (event: any) => {
            if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
              console.log('🔔 Notification clicked from Service Worker:', event.data);
              if (notificationCallbackRef.current && event.data.title && event.data.body) {
                notificationCallbackRef.current({
                  title: event.data.title,
                  body: event.data.body,
                  data: event.data.data || {},
                });
              }
            }
          };
          
          navigator.serviceWorker.addEventListener('message', clickHandler);
        }

        // Note: Notification clicks are handled by Service Worker
        // The Service Worker will send messages to the page when notifications are clicked

        setIsInitialized(true);
        console.log('OneSignal initialized successfully', {
          permission: 'Notification' in window ? Notification.permission : 'not supported',
          oneSignalAvailable: !!OneSignal,
        });
      } catch (error) {
        console.error('Error initializing OneSignal:', error);
        // Still mark as initialized to avoid infinite retries
        setIsInitialized(true);
      }
    };

    initOneSignal();

    // Cleanup function
    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (permissionInterval) clearInterval(permissionInterval);
    };
  }, []);

  const subscribe = async () => {
    if (!window.OneSignal || !isInitialized) {
      console.warn('OneSignal is not initialized');
      return;
    }

    try {
      // Check current permission using browser API
      if (!('Notification' in window)) {
        console.warn('Notifications are not supported in this browser');
        return;
      }

      const permission = Notification.permission;
      
      if (permission === 'default') {
        // Request permission using browser API
        const result = await Notification.requestPermission();
        setIsSubscribed(result === 'granted');
        
        if (result === 'granted') {
          // Try to get user ID after permission is granted
          setTimeout(async () => {
            try {
              if (typeof window.OneSignal?.getUserId === 'function') {
                const id = await window.OneSignal.getUserId();
                if (id) setUserId(id);
              }
            } catch (e) {
              console.log('Could not get user ID:', e);
            }
          }, 1000);
        }
      } else if (permission === 'granted') {
        setIsSubscribed(true);
        // Try to get user ID
        try {
          if (typeof window.OneSignal?.getUserId === 'function') {
            const id = await window.OneSignal.getUserId();
            if (id) setUserId(id);
          }
        } catch (e) {
          console.log('Could not get user ID:', e);
        }
      } else {
        console.log('Notification permission denied');
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error subscribing to OneSignal:', error);
    }
  };

  const unsubscribe = async () => {
    if (!window.OneSignal || !isInitialized) {
      console.warn('OneSignal is not initialized');
      return;
    }

    try {
      // In OneSignal v16, we can't programmatically unsubscribe
      // The user needs to do it from browser settings
      // But we can update our local state
      setIsSubscribed(false);
      console.log('To fully unsubscribe, please disable notifications in your browser settings');
    } catch (error) {
      console.error('Error unsubscribing from OneSignal:', error);
    }
  };

  // Update callback ref when it changes
  useEffect(() => {
    notificationCallbackRef.current = onNotificationReceived;
  }, [onNotificationReceived]);

  const setNotificationCallback = (callback: ((notification: any) => void) | undefined) => {
    setOnNotificationReceived(callback);
  };

  return {
    isInitialized,
    userId,
    isSubscribed,
    subscribe,
    unsubscribe,
    onNotificationReceived,
    setOnNotificationReceived: setNotificationCallback,
  };
};

